import { useState, useCallback } from 'react';
import { Spinner } from '@/components/ui/Spinner';
import { FileInput } from './FileInput';
import type { UploadState } from '@/hooks/useAttachmentUpload';

interface ReplyBoxProps {
  onSend: (text: string, idempotencyKey: string) => void;
  isSending: boolean;
  disabled?: boolean;
  // Attachment props
  onFileSelect?: (file: File) => void;
  uploadState?: UploadState;
  uploadProgress?: number;
  pendingFile?: File | null;
  onCancelUpload?: () => void;
  uploadError?: Error | null;
  onRetryUpload?: () => void;
}

export function ReplyBox({
  onSend,
  isSending,
  disabled,
  onFileSelect,
  uploadState = 'idle',
  uploadProgress = 0,
  pendingFile,
  onCancelUpload,
  uploadError,
  onRetryUpload,
}: ReplyBoxProps) {
  const [text, setText] = useState('');

  const isUploading = uploadState === 'init' || uploadState === 'uploading';
  const isUploadError = uploadState === 'error';

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const trimmedText = text.trim();
      if (!trimmedText || isSending || isUploading) return;

      // Generate idempotency key
      const idempotencyKey = crypto.randomUUID();
      onSend(trimmedText, idempotencyKey);
      setText('');
    },
    [text, isSending, isUploading, onSend]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit(e);
      }
    },
    [handleSubmit]
  );

  return (
    <form onSubmit={handleSubmit} className="border-t border-gray-200 px-4 py-3 bg-white">
      {/* Pending file preview / Upload progress */}
      {pendingFile && (
        <div className="mb-2 flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg text-sm">
          <svg
            className="w-4 h-4 text-gray-400 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
            />
          </svg>

          <div className="flex-1 min-w-0">
            <p className="truncate text-gray-700">{pendingFile.name}</p>
            {isUploading && (
              <div className="mt-1 h-1 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-600 transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            )}
            {isUploadError && uploadError && (
              <p className="text-xs text-red-500 mt-0.5">
                {uploadError.message}
                {onRetryUpload && (
                  <button
                    type="button"
                    onClick={onRetryUpload}
                    className="ml-2 underline hover:no-underline"
                  >
                    Retry
                  </button>
                )}
              </p>
            )}
          </div>

          {!isUploading && onCancelUpload && (
            <button
              type="button"
              onClick={onCancelUpload}
              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded transition-colors"
              title="Remove file"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}

          {isUploading && (
            <Spinner size="sm" />
          )}
        </div>
      )}

      <div className="flex items-end gap-2">
        {/* File attachment button */}
        {onFileSelect && (
          <FileInput
            onSelect={onFileSelect}
            disabled={disabled || isSending || isUploading}
          />
        )}

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message..."
          disabled={disabled || isSending || isUploading}
          rows={1}
          className="flex-1 resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500 max-h-32"
        />

        <button
          type="submit"
          disabled={disabled || isSending || isUploading || !text.trim()}
          className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
        >
          {isSending || isUploading ? (
            <Spinner size="sm" />
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          )}
        </button>
      </div>
    </form>
  );
}
